import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { fromUnixSeconds } from 'shared-components';
import { useCallback, useEffect, useState } from 'react';
import { IAuditLog, IEmail } from 'types';
import { AuthState } from '../../../../store/auth/types';
import { isEnrolyStaff } from '../../../../utilities/roles/permissions';
import Search from '../../../atoms/search';
import { useTranslation } from "react-i18next";

/**
 * Strips SAML provider prefix from a username (e.g., "GoogleSAML_user@email.com" -> "user@email.com")
 */
function stripSamlPrefix(username: string): string {
  const samlPrefixMatch = username.match(/^[A-Za-z]+SAML_(.+)$/);
  return samlPrefixMatch ? samlPrefixMatch[1] : username;
}

/**
 * Formats the actor display name from backend-sanitized audit log data.
 * The backend redacts usernames for non-privileged viewers by setting who=alias.
 * If who differs from alias, the viewer is privileged and sees both.
 */
function formatActorDisplay(log: IAuditLog): string {
  if (log.alias && log.alias !== log.who) {
    return `${log.alias} (${stripSamlPrefix(log.who)})`;
  }
  return stripSamlPrefix(log.who);
}

interface AuditlogProps {
  auth: AuthState;
  auditLog: IAuditLog[];
  emails: IEmail[];
  sortLogEntries: (a: IAuditLog | IEmail, b: IAuditLog | IEmail) => number;
}

export function Auditlog(props: AuditlogProps) {
    const { t } = useTranslation('tables');
  const { sortLogEntries, auth, auditLog, emails } = props;
  const filterRecordedInterviewAuditlogMessages = useCallback(
    (logs: IAuditLog[]) => {
      const isInterviewAssessmentLog = (log: IAuditLog) =>
        log.action.includes('assessed') ||
        log.action.includes('revealed to university') ||
        log.action.includes('has been revised from') ||
        log.action.includes('question score or explanation updated');

      if (isEnrolyStaff(auth.role)) {
        return logs;
      }

      return logs.filter(log => !(isInterviewAssessmentLog(log) || ['Admin', 'Enroly'].includes(log.who)));
    },
    [auth.role],
  );

  const [auditLogRecords, setAuditLogRecords] = useState(filterRecordedInterviewAuditlogMessages(auditLog));
  const [emailRecords, setEmailRecords] = useState(emails);
  const [auditLogSearch, setAuditLogSearch] = useState('');

  useEffect(() => {
    if (auditLogSearch === '') {
      setAuditLogRecords(filterRecordedInterviewAuditlogMessages(auditLog));
      setEmailRecords(emails);
    } else {
      setAuditLogRecords(
        filterRecordedInterviewAuditlogMessages(auditLog).filter(log =>
          `${log.who}${log.role}${log.action}`.toLowerCase().includes(auditLogSearch.toLowerCase()),
        ),
      );
      setEmailRecords(
        emails.filter(
          log =>
            `email${log.to[0]}${log.subject}${log.from}`.toLowerCase().includes(auditLogSearch.toLowerCase()) ||
            'system'.includes(auditLogSearch.toLowerCase()),
        ),
      );
    }
  }, [auditLogSearch, filterRecordedInterviewAuditlogMessages, auditLog, emails]);

  return (
    <Box>
      <Box mb={3} id="audit-log-search-box">
        <Search
          currentSearch={auditLogSearch}
          hasSearchSign={true}
          placeholder={t('tables:searchActivity')}
          onSearch={(searchValue: string) => setAuditLogSearch(searchValue)}
        />
      </Box>

      <Box>
        <Table size={'sm'} id="audit-log-table" sx={{ tableLayout: 'fixed' }}>
          <Thead>
            <Tr>
              <Th pl={0} width={'9rem'}>
                {t('tables:time')}
                                            </Th>
              <Th width={{ base: '6.5rem', '2xl': '7.5rem' }}>{t('tables:role')}</Th>
              <Th width={'30%'}>{t('tables:actor')}</Th>
              <Th pr={0}>{t('tables:activity')}</Th>
            </Tr>
          </Thead>

          <Tbody>
            {[...auditLogRecords, ...emailRecords]
              .sort((a, b) => sortLogEntries(a, b))
              .map((log: IAuditLog | any, index: number) => {
                if (log.hasOwnProperty('when')) {
                  return (
                    <Tr key={`${log.when}.${index}`}>
                      <Td pl={0}>
                        {fromUnixSeconds(log.when).toZonedDateTimeISO('UTC').toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </Td>
                      <Td>{log.role.replace('[', '').replace(']', '')}</Td>
                      <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                        {formatActorDisplay(log)}
                      </Td>
                      <Td pr={0}>{log.action.replace(/\*/g, '')}</Td>
                    </Tr>
                  );
                }
                return (
                  <Tr key={`${log.lastEmailTime}.${index}`}>
                    <Td pl={0}>
                      {fromUnixSeconds(Math.floor((log?.sentActionTime ?? 0) / 1000))
                        .toZonedDateTimeISO('UTC')
                        .toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                    </Td>
                    <Td>{'System'}</Td>
                    <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                      {log.to[0]}
                    </Td>
                    <Td pr={0} color={getEmailRowColor(log)}>
                      {getEmailRowText(log)}
                    </Td>
                  </Tr>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

function getEmailRowColor(log: IEmail) {
  if (log.isComplaint) return 'error';
  else if (log.isBounced) return 'warning';
  else if (log.isRead) return 'success';
  else if (log.isDelivered) return 'info';
  return undefined;
}

function getEmailRowText(log: IEmail) {
  let message = '';
  if (log.isComplaint) {
    message = 'Complaint';
  } else if (log.isBounced) {
    message = 'Bounced';
  } else if (log.isRead) {
    message = 'Read';
  } else if (log.isDelivered) {
    message = 'Delivered';
  } else {
    message = 'Sent';
  }
  return `Email ${message}: ${log.subject}`;
}
